<template>
    <div class="bg-white rounded shadow">

        <GoBackButtonRow class="position-relative top-6 left-6" />

        <Modal
            :text="'modalText'"
            id="modal_show"
        />

        <div class="container grid grid-cols-1 md:grid-cols-3 mt-5 gap-3">
            <div class="position-relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute top-1 md:-top-3 ml-2">
                    {{ $t('sale.discount.discountName') }}
                </div>
                <InputBox
                    class="px-0"
                    model-name="name"
                    :model-form="modelForm"
                />
            </div>

            <div class="position-relative mt-4 mt-md-0">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('sale.discount.startDate') }}
                </div>
                <InputBox
                    class="px-0"
                    :model-form="modelForm"
                    model-name="startAt"
                    :no-margin-top="true"
                    type="date"
                />
            </div>

            <div class="position-relative mt-4 mt-md-0">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('sale.discount.endDate') }}
                </div>
                <InputBox
                    class="px-0"
                    :model-form="modelForm"
                    model-name="endAt"
                    :no-margin-top="true"
                    type="date"
                />
            </div>
        </div>

        <div class="container grid grid-cols-1 md:grid-cols-3 mt-5 gap-3">
            <div class="col-span-2 position-relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('sale.discount.aboutDiscount') }}
                </div>
                <b-form-textarea
                    required
                    class="border-gray-300 focus:ring-0"
                    rows="5"
                    v-model="modelForm.definition"
                />
            </div>

            <div class="col-span-1 position-relative mt-4 mt-md-0">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('sale.discount.aboutDiscount') }}
                </div>
                <b-form-select v-model="modelForm.isActive" class="focus:ring-0 border-gray-300 h-11">
                    <b-form-select-option :value="true">{{ $t('active') }}</b-form-select-option>
                    <b-form-select-option :value="false">{{ $t('notActive') }}</b-form-select-option>
                </b-form-select>
            </div>
        </div>

        <div class="bg-mediumGray w-full h-10 mt-4"></div>

        <div class="text-2xl mt-3 ml-3">
            {{ $t('sale.discount.discountCondition') }}
        </div>

        <div :class="modelForm.isForAllProducts ? 'container grid grid-cols-1 md:grid-cols-2 mt-5' : 'container grid grid-cols-1 md:grid-cols-3 mt-5'">
            <div :class="modelForm.isForAllProducts ? 'position-relative col-span-3 md:col-span-1' : 'position-relative col-span-3 md:col-span-1'">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('sale.discount.conditionsForDiscountingProducts') }}
                </div>
                <v-select
                    class="d-block"
                    :options="[{'label': $t('sale.discount.forAllProducts'), 'value': true}, {'label': $t('sale.discount.forSomeProducts'), 'value': false}]"
                    v-model="modelForm.isForAllProducts"
                    :reduce="options => options.value"
                    :clearable="false"
                >
                </v-select>
            </div>

            <div v-if="modelForm.isForAllProducts" class="position-relative mt-4 mt-md-0">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute top-1 md:-top-3 ml-2 ml-md-4">
                    {{ $t('sale.discount.amountForDiscount') }}
                </div>
                <InputBox
                    class="px-0 pl-md-3"
                    model-name="forTotalSum"
                    type="number"
                    :model-form="modelForm"
                />
            </div>

            <template v-if="!modelForm.isForAllProducts">
                <div v-for="(discount, index) in getDiscountProductsByBonus(false)" :key="index" class="md:col-start-2 col-span-2 md:col-span-2 position-relative grid grid-cols-3 md:grid-cols-7 mt-5 mt-md-0 gap-3">
                    <div class="col-span-3 md:col-span-4 mb-3 ml-md-3">
                        <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                            {{ $t('product.title') }}
                        </div>
                        <v-select
                            :options="$route.name === 'sales/discounts/edit' ? [getProductsInWarehouses.find(piw => piw.product['@id'] === discount.product)] : getProductsInWarehouses"
                            v-model="discount.product"
                            :reduce="options => $route.name === 'sales/discounts/edit' ? options.product['@id'] : options.product"
                            :get-option-label="option => $route.name === 'sales/discounts/edit' ? option.product.name : !isFirstAttemt ? option.product.name : option.name"
                            :clearable="false"
                            :disabled="$route.name === 'sales/discounts/edit'"
                            @click.native="changeOptionLabel(discount.product)"
                        >
                            <template #no-options="{}">
                                {{ $t("selectError") }}
                            </template>
                            <template #option="{ product }">
                                <h6 style="margin: 0">{{ product.name }}</h6>
                            </template>
                        </v-select>
                    </div>

                    <div class="position-relative col-span-2 md:col-span-2">
                        <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute top-1 md:-top-3 ml-2">
                            {{ $t('amount') }}
                        </div>

                        <InputBox
                            class="px-0"
                            model-name="amountForDiscount"
                            :model-form="discount"
                        />
                    </div>

                    <div class="col-span-1 flex mt-3 mt-md-0">
                        <div
                            v-if="index + 1 === getDiscountProductsByBonus(false).length && $route.name !== 'sales/discounts/edit'"
                            @click="addNewProduct(false)" class="mr-2">
                            <img src="/images/more.png" alt="" class="h-10 cursor-pointer">
                        </div>
                        <div
                            v-if="getDiscountProductsByBonus(false).length > 1"
                            @click="deleteRowTable(discount)">
                            <img src="/images/minus.png" alt="" class="h-10 cursor-pointer">
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <div class="container grid grid-cols-2 mt-5">
            <div class="position-relative col-span-2 md:col-span-1">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('sale.discount.discountParticipants') }}
                </div>
                <v-select
                    class="d-block"
                    :options="[{'label': $t('sale.discount.forAllCustomers'), 'value': true}, {'label': $t('sale.discount.forSomeCustomers'), 'value': false}]"
                    v-model="modelForm.isForAllUsers"
                    :reduce="options => options.value"
                    :clearable="false"
                >
                </v-select>

            </div>

            <div v-if="!modelForm.isForAllUsers" class="position-relative col-span-2 md:col-span-1 mt-5 mt-md-0 ml-md-3">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('customer.choose') }}
                </div>
                <v-select
                    multiple
                    v-model="modelForm.privilegedUsers"
                    class="d-block"
                    :options="getCustomers"
                    :clearable="false"
                    :reduce="options => options['@id']"
                    :get-option-label="customer =>
                        customer.isCompany ? customer.customerCompany.name :
                         customer.person ? customer.person.givenName + ' ' + customer.person.familyName : ''"
                >
                </v-select>
            </div>
        </div>

        <div class="container grid grid-cols-12 position-relative mt-5">
            <div class="col-span-10 font-bold opacity-80 px-1 text-fontColor z-30 position-absolute -top-5 ml-4">
                {{ $t('sale.discount.discountDays') }}
            </div>

            <div class="d-flex" v-for="(day, index) in weekday" :key="index">
                <ul class="ks-cboxtags">
                    <li class="">
                        <input type="checkbox" :id="day.val" v-model="activeDays" :value="day.val">
                        <label class="text-xs py-md-2 md:text-base md:text-center" :for="day.val">{{ $t(day.label).substr(0, 3) }}</label>
                    </li>
                </ul>
            </div>
        </div>

        <div class="bg-mediumGray w-full h-10 mt-2"></div>


        <div class="text-2xl mt-3 ml-3">
            {{ $t('sale.discount.discountBonus') }}
        </div>

        <div :class="modelForm.discountType === 'product' ? 'container grid grid-cols-1 md:grid-cols-3 mt-5' : 'container grid grid-cols-2 mt-5'">
            <div class="position-relative col-span-3 md:col-span-1">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('sale.discount.customersGet') }}
                </div>
                <v-select
                    class="d-block"
                    :options="[
                             {'label': $t('sale.discount.discountPercent'), 'value': 'percent'},
                             {'label': $t('sale.discount.discountSum'), 'value': 'sum'},
                             {'label': $t('sale.discount.discountProduct'), 'value': 'product'},
                         ]"
                    v-model="modelForm.discountType"
                    :reduce="options => options.value"
                    :clearable="false"
                >
                </v-select>
            </div>

            <div v-if="modelForm.discountType !== 'product'" class="position-relative col-span-2 md:col-span-1 mx-md-3 mt-4 mt-md-0">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute md:-top-3 ml-2">
                    <span v-if="modelForm.discountType === 'percent'">{{ $t('sale.discount.percentAmount') }}</span>
                    <span v-else-if="modelForm.discountType === 'sum'">{{ $t('sale.discount.sumAmount') }}</span>
                </div>

                <InputBox
                    v-if="modelForm.discountType === 'percent'"
                    class="px-0"
                    type="number"
                    model-name="discountPercent"
                    :model-form="modelForm"
                />

                <InputBox
                    v-else-if="modelForm.discountType === 'sum'"
                    class="px-0"
                    type="number"
                    model-name="discountSum"
                    :model-form="modelForm"
                />
            </div>

            <template v-if="getDiscountProductsByBonus(true).length > 0">
                <div v-for="(discount, index) in getDiscountProductsByBonus(true)" :key="index" class="md:col-start-2 col-span-2 position-relative grid grid-cols-3 md:grid-cols-7 gap-3 mt-5 mt-md-0">
                    <div class="col-span-6 md:col-span-4 mb-3 ml-md-3">
                        <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                            {{ $t('product.title') }}
                        </div>
                        <v-select
                            :options="$route.name === 'sales/discounts/edit' ? [getProductsInWarehouses.find(piw => piw.product['@id'] === discount.product)] : getProductsInWarehouses"
                            v-model="discount.product"
                            :reduce="options => $route.name === 'sales/discounts/edit' ? options.product['@id'] : options.product"
                            :get-option-label="option => $route.name === 'sales/discounts/edit' ? option.product.name : !isFirstAttemt ? option.product.name : option.name"
                            :clearable="false"
                            :disabled="$route.name === 'sales/discounts/edit'"
                            @click.native="changeOptionLabel(discount.product)"
                        >
                            <template #no-options="{}">
                                {{ $t("selectError") }}
                            </template>
                            <template #option="{ product }">
                                <h6 style="margin: 0">{{ product.name }}</h6>
                            </template>
                        </v-select>
                    </div>

                    <div class="position-relative col-span-4 md:col-span-2">
                        <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                            {{ $t('amount') }}
                        </div>

                        <InputBox
                            class="px-0"
                            model-name="amountForDiscount"
                            :model-form="discount"
                        />
                    </div>

                    <div class="col-span-1 mt-3 mt-md-0 flex">
                        <div
                            v-if="index + 1 === getDiscountProductsByBonus(true).length"
                            @click="addNewProduct(true)" class="mr-2">
                            <img src="/images/more.png" alt="" class="h-10 cursor-pointer">
                        </div>
                        <div
                            v-if="getDiscountProductsByBonus(true).length > 1"
                            @click="deleteRowTable(discount)">
                            <img src="/images/minus.png" alt="" class="h-10 cursor-pointer">
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <SubmitRow
            class="ml-1 mt-5 pb-5"
            btn-variant="btn btn-primary"
            :is-disabled="checkForDisabling"
            text="save"
            sm-size="6"
            md-size="6"
            :do-action="addDiscount"
            :is-loading="isLoading"
        />
    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import SubmitRow from "@/components/SubmitRow";
import Modal from "@/components/html/Modal";
import InputBox from "@/components/InputBox";
import '@/style/vue-select.css';
import vSelect from 'vue-select'
import GoBackButtonRow from "@/components/GoBackButtonRow";

export default {
    name: "DiscountsAdd",
    components: {
        Modal,
        InputBox,
        vSelect,
        GoBackButtonRow,
        SubmitRow,
    },
    data() {
        return {
            isLoading: false,
            breadcrumbItems: [
                {name: 'home', url: ''},
                {name: 'sale.title', url: 'sales/sale'},
                {name: 'sale.discount.title', url: 'sales/discounts'},
                {name: 'add'}
            ],
            isFirstAttemt: false,
            modelForm: {
                isActive: false,
                discountId: null,
                definition: '',
                startAt: '',
                endAt: '',
                discountSum: '',
                discountPercent: '',
                name: '',
                discountType: 'sum',
                privilegedUsers: [],
                isForAllProducts: true,
                isForAllUsers: true,
                forTotalSum: '',
                discountProducts: [],
                activeDays: ''
            },
            activeDays: ["sun", "mon", "tue", "wed", "thu", "fri", "sat"],
            weekday: [
                {val: "sun", label: 'statistics.weekday.sun'},
                {val: "mon", label: 'statistics.weekday.mon'},
                {val: "tue", label: 'statistics.weekday.tue'},
                {val: "wed", label: 'statistics.weekday.wed'},
                {val: "thu", label: 'statistics.weekday.thu'},
                {val: "fri", label: 'statistics.weekday.fri'},
                {val: "sat", label: 'statistics.weekday.sat'},
            ],
        }
    },
    computed: {
        ...mapGetters([
            'getProductsInWarehouse',
            'getProductsInWarehouses',
            'getCustomers'
        ]),
        checkForDisabling() {
            return !(
                this.modelForm.name &&
                this.modelForm.startAt &&
                this.modelForm.endAt &&
                this.modelForm.definition &&
                this.checkProductsData &&
                this.activeDays &&
                this.checkCustomerData &&
                this.checkDiscountBonus
            )
        },
        checkProductsData() {
            if (this.modelForm.isForAllProducts && this.modelForm.forTotalSum) {
                return true
            } else return !this.modelForm.isForAllProducts && !this.modelForm.discountProducts.find(dp => !dp.product || !dp.amountForDiscount);

        },
        checkCustomerData() {
            if (this.modelForm.isForAllUsers) {
                return true
            } else return this.modelForm.privilegedUsers.length > 0;
        },
        checkDiscountBonus() {
            let result = true

            switch (this.modelForm.discountType) {
                case "sum":
                    result = !!this.modelForm.discountSum;
                    break;

                case "product":
                    result = !this.modelForm.discountProducts.find(dp => !dp.product || !dp.amountForDiscount)
                    break;

                case "percent":
                    result = !!this.modelForm.discountPercent
                    break;
            }

            return result;
        }
    },
    methods: {
        ...mapActions([
            'fetchAllProductsInWarehouses',
            'putDiscount',
            'fetchDiscount',
            'fetchCustomers',
            'pushDiscount',
            'setBreadcrumbItems'
        ]),
        changeOptionLabel(d) {
            if (d !== undefined) {
                this.isFirstAttemt = true
            }
        },
        getDiscountProductsByBonus(isBonus) {
            return this.modelForm.discountProducts.filter(d => d.isBonus === isBonus)
        },
        addNewProduct(isBonus) {
            this.isFirstAttemt = false

            if (this.hasEmptyInputs(isBonus)) {
                this.modelForm.discountProducts.push({isBonus: isBonus})
            }
        },
        hasEmptyInputs(isBonus) {
            return this.modelForm.discountProducts.filter(
                d => d.isBonus === isBonus &&
                    (!d.product || !d.amountForDiscount)
            ).length === 0
        },
        searchProductByBarCode() {
            if (this.search.barCode.length === 13) {
                this.isNeedFilter = false
                this.hasDelayed = true

                let piw = this.getProductsInWarehouses.find(piw => this.search.barCode === piw.product.barCode)

                if (piw === undefined) {
                    this.isNeedFilter = true

                    setTimeout(() => {
                        this.search.barCode = ""
                    }, 1500)
                } else {
                    this.addProductToDtos(piw)

                    setTimeout(() => {
                        this.search.barCode = ""
                    }, 200)
                    setTimeout(() => {
                        this.hasDelayed = false
                        this.isNeedFilter = true
                    }, 500)
                }
            }
        },
        deleteRowTable(discount) {
            let index = this.modelForm.discountProducts.findIndex(item => item === discount)

            this.modelForm.discountProducts.splice(index, 1)
        },
        showUnitOfMeasure(unit) {
            if (unit === undefined) {
                return ''
            }

            if (Object.prototype.hasOwnProperty.call(this.$i18n.messages.en.unitOfMeasure, unit)) {
                return this.$t('unitOfMeasure.' + unit)
            }

            return unit
        },
        addDiscount() {
            this.$root.showOverlay = true

            let date = ''

            this.activeDays.map((day) => date = date + ' ' + day)

            this.modelForm.activeDays = date.substr(1);

            this.modelForm.discountSum = parseInt(this.modelForm.discountSum)
            this.modelForm.discountPercent = parseInt(this.modelForm.discountPercent)

            this.modelForm.discountProducts.map(p => {
                p.amountForDiscount = parseInt(p.amountForDiscount)
                if (this.$route.name !== "sales/discounts/edit") {
                    p.product = p.product['@id']
                }
            })

            if (this.$route.name === "sales/discounts/edit") {
                this.putDiscount(this.modelForm)
                    .then(() => {
                        this.$root.showOverlay = false
                        this.$i18nHelper.toUrl('sales/discounts')
                    })
                    .catch(() => {
                        this.$root.showOverlay = false
                    })
            } else {
                this.pushDiscount(this.modelForm)
                    .then(() => {
                        this.$root.showOverlay = false
                        this.$i18nHelper.toUrl('sales/discounts')
                    })
                    .catch(() => {
                        this.$root.showOverlay = false
                    })
            }
        },
    },
    watch: {
        'modelForm.isForAllProducts'() {
            if (!this.modelForm.isForAllProducts) {
                if (this.getDiscountProductsByBonus(false).length === 0) {
                    this.modelForm.discountProducts.push({isBonus: false})
                }
            } else {
                this.modelForm.discountProducts = this.getDiscountProductsByBonus(true)
            }
        },
        'modelForm.discountType'() {
            if (this.modelForm.discountType === 'product') {
                if (this.getDiscountProductsByBonus(true).length === 0) {
                    this.modelForm.discountProducts.push({isBonus: true})
                }
            } else {
                this.modelForm.discountProducts = this.getDiscountProductsByBonus(false)
            }
        }
    },
    created() {
        if (this.$route.name === "sales/discounts/edit") {
            this.breadcrumbItems =
                [
                    {name: 'home', url: ''},
                    {name: 'sale.title', url: 'sales/sale'},
                    {name: 'sale.discount.title', url: 'sales/discounts'},
                    {name: 'edit'}
                ]
        }

        this.setBreadcrumbItems(this.breadcrumbItems)
    },
    mounted() {
        this.$root.showOverlay = true
        this
            .fetchAllProductsInWarehouses()
            .then(() => {
                this.fetchCustomers(null)
                if (this.$route.name === "sales/discounts/edit") {
                    this.fetchDiscount(this.$route.params.id)
                        .then((response) => {
                            this.modelForm.isActive = !!response.isActive
                            this.modelForm.company = response.company
                            this.modelForm.discountPercent = response.discountPercent
                            this.modelForm.name = response.name
                            this.modelForm.definition = response.definition
                            this.modelForm.discountSum = response.discountSum
                            this.modelForm.isDiscountPercent = response.isDiscountPercent

                            response.privilegedUsers.forEach((customer) => {
                                this.modelForm.privilegedUsers.push(customer['@id'])
                            })

                            this.modelForm.isForAllProducts = response.isForAllProducts
                            this.modelForm.isForAllUsers = response.isForAllUsers
                            this.modelForm.forTotalSum = response.forTotalSum
                            this.modelForm.activeDays = response.activeDays
                            this.modelForm.discountType = response.discountType
                            this.modelForm.discountId = response.id
                            this.modelForm.startAt = response.startAt.substr(0, 10)
                            this.modelForm.endAt = response.endAt.substr(0, 10)
                            this.activeDays = []

                            if (response.activeDays) {
                                response.activeDays.split(' ').map(w => {
                                    this.activeDays.push(w)
                                })
                            }

                            response.discountProducts.map(p => {
                                let data = {}
                                data.product = p.product['@id']
                                data.amountForDiscount = p.amountForDiscount
                                data.isBonus = p.isBonus

                                this.modelForm.discountProducts.push(data)
                            })
                        })
                }

                this.$root.showOverlay = false
            })
    },
    beforeDestroy() {
        this.$root.showOverlay = false
    },
}
</script>

<style scoped>

ul.ks-cboxtags li{
    display: inline;
}

ul.ks-cboxtags {
    padding: 0 5px;
}

ul.ks-cboxtags li label{
    background-color: #F6F7FB;
    border: none;
    color: #444444;
    border-radius: 5px;
}

ul.ks-cboxtags li label {
    padding: 6px 8px 8px 8px;
    cursor: pointer;
    margin-top: 5px;
    width: 40px;

}

ul.ks-cboxtags li label::before {
    display: inline-block;
    font-style: normal;
    font-variant: normal;
    text-rendering: auto;
    -webkit-font-smoothing: antialiased;
    font-weight: 900;
    font-size: 10px;
    padding: 2px 6px 2px 2px;
    transition: transform .3s ease-in-out;
}

ul.ks-cboxtags li input[type="checkbox"]:checked + label {
    border: none;
    background-color: #FF8A5A;
    color: #fff;
    transition: all .2s;
}

ul.ks-cboxtags li input[type="checkbox"] {
    position: absolute;
    opacity: 0;

}

.text-light span {
    font-size: 12px;
}

.delete-row {
    height: 25px;
    width: 25px;
    border: 1px solid #c5c5c5;
    background-color: #6f42c1;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.awesome-icon-amount {
    color: #FFFFFF;
    font-weight: 100;
    font-size: 15px;
}

</style>
